import { useState, useCallback, useEffect } from 'react'

interface Submission {
  id: string
  lenderId: string
  lenderName?: string
  status: string
  createdDate: string
}

interface Opportunity {
  id: string
  name: string
  businessLegalName?: string
  amount?: number
  closeDate?: string
  createdDate: string
  submissions: Submission[]
}

interface ISOInfo {
  id: string
  email: string
  name: string
  companyName: string
}

interface AdminISODealsResponse {
  opportunities: Opportunity[]
  totalCount: number
  fieldMappings: any[]
  isoInfo: ISOInfo
}

interface UseAdminISODealsReturn {
  data: AdminISODealsResponse | null
  loading: boolean
  error: string | null
  fetchDeals: (isoId: string) => Promise<void>
  clearData: () => void
  refetch: () => Promise<void>
}

// Overloaded function signatures
export function useAdminISODeals(): UseAdminISODealsReturn
export function useAdminISODeals(isoId: string): UseAdminISODealsReturn
export function useAdminISODeals(isoId?: string): UseAdminISODealsReturn {
  const [data, setData] = useState<AdminISODealsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDeals = useCallback(async (isoId: string) => {
    try {
      setLoading(true)
      setError(null)
      
      console.log(`🔍 Fetching deals for ISO: ${isoId}`)

      const response = await fetch(`/api/admin/isos/${isoId}/deals`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      console.log(`📡 API Response status: ${response.status}`)

      if (!response.ok) {
        const errorData = await response.json()
        console.error('❌ API Error:', errorData)
        throw new Error(errorData.message || 'Failed to fetch ISO deals')
      }

      const result = await response.json()
      console.log('✅ API Response data:', result)
      setData(result.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred'
      setError(errorMessage)
      console.error('❌ Error fetching admin ISO deals:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const clearData = useCallback(() => {
    setData(null)
    setError(null)
  }, [])

  const refetch = useCallback(async () => {
    if (isoId) {
      await fetchDeals(isoId)
    }
  }, [isoId, fetchDeals])

  // Auto-fetch when isoId is provided
  useEffect(() => {
    if (isoId) {
      fetchDeals(isoId)
    }
  }, [isoId, fetchDeals])

  return {
    data,
    loading,
    error,
    fetchDeals,
    clearData,
    refetch
  }
}
